import { action, computed, flow, makeObservable, observable, reaction, when } from 'mobx';
import EVMBase, { EVMError } from '../Wallets/EVMBase';
import type ExplorerRootStore from '../../ExplorerRootStore';
import { EVM_NS, EVM_NS as NAMESPACE } from './Client';
import type UniversalProvider from '@walletconnect/universal-provider';
import type { RequestArguments } from '@walletconnect/universal-provider';
import { requestChangeWalletChain } from '../Wallets/helper';
import { Provider } from '../../types';

export class WalletConnectError extends Error {
	static provider(): WalletConnectError {
		return new WalletConnectError('WalletConnect provider is undefined');
	}
}

export default class WalletConnectEVM extends EVMBase {
	readonly type = 'evm';
	readonly name = 'WalletConnect';
	readonly icon = 'https://cdn.mayan.finance/stores/wallet-connect.png';
	readonly info = undefined;
	readonly website = 'https://walletconnect.com';

	#root: ExplorerRootStore;

	override supportedChainIds: readonly number[] | undefined = undefined;
	override chainId: number = 1;

	constructor(root: ExplorerRootStore) {
		super(undefined);
		this.#root = root;

		makeObservable(this, {
			uri: computed,
			initialized: computed,
			supportedChainIds: observable,
			universal: computed,
		});
		reaction(
			() => this.universal,
			(next, prev) => {
				if (prev) this.#removeUniveralListeners(prev);
				if (next) this.#addUniveralListeners(next);
				this.setNamespaceChainsIds();
			},
		);

		const parentRefresh = this.refresh.bind(this);
		this.refresh = flow(function* (this: WalletConnectEVM) {
			if (!this.initialized) yield when(() => this.initialized);
			yield parentRefresh();
		});
	}

	isEVM(): this is EVMBase {
		return true;
	}

	_isConnected() {
		if (!this.universal?.client) return false;
		return this.universal.client.core.relayer.connected;
	}

	get universal() {
		return this.client?.evmProvider;
	}

	get client() {
		return this.#root.wallets?.walletconnect;
	}

	get initialized() {
		return this.client?.initializing === false;
	}

	get uri() {
		return this.client?.displayUri;
	}

	get isInstalled() {
		return this.#root.wallets.walletconnectEnabled;
	}

	get disconnectable() {
		return true;
	}

	reset = action(() => {
		this.chainId = this.supportedChainIds?.[0] ?? 1;
		this.accounts.clear();
	});

	setNamespaceChainsIds = action(() => {
		const sessionChainIds =
			this.universal?.session?.namespaces[NAMESPACE].accounts.map((account) => {
				return Number.parseInt(account.split(':')[1] || '');
			}) ?? [];
		if (sessionChainIds.length === 0) {
			this.supportedChainIds = undefined;
			return;
		}
		const allChainIds = this.client?.evmChains.map((c) => c.chainId) ?? [];
		this.supportedChainIds = allChainIds.filter((id) => sessionChainIds.includes(id));
	});

	loadEthereum() {
		const provider = this.universal;
		if (!provider) return;
		return {
			request: this.#request,
			on: provider.on.bind(provider) as Provider['on'],
			removeListener: provider.removeListener.bind(provider) as Provider['removeListener'],
		};
	}

	connect = flow(function* (this: WalletConnectEVM, quick = false) {
		if (!this.client) throw WalletConnectError.provider();

		try {
			yield this.client.waitForReady();
			if (!(this.isReady && quick)) {
				yield this.client.connectEvm();
				yield this.fetchAccounts();
				yield this.fetchChainId();
			} else {
				yield this.refresh();
			}
		} catch (error) {
			console.error('Failed to connect WalletConnect:', error);
			throw error;
		}
	});

	disconnect = flow(function* disconnect(this: WalletConnectEVM) {
		const provider = this.injectedProvider;
		if (provider) {
			try {
				yield this.client?.disconnectEvm();
			} catch (error) {
				console.error('Failed to disconnect WalletConnect:', error);
			}
		}
		this.reset();
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	#request = <T = unknown>(args: RequestArguments, expiry?: number): Promise<T> => {
		return this.universal!.request(args, this.formatChainId(this.chainId), expiry);
	};

	changeWalletChain(chain: Chain) {
		if (!this.injectedProvider) {
			throw EVMError.provider();
		}
		return requestChangeWalletChain(chain, { request: this.#request });
	}

	private formatChainId(chainId: number): string {
		return `${EVM_NS}:${chainId}`;
	}

	#addUniveralListeners(provider: UniversalProvider): void {
		provider.on('session_update', this.setNamespaceChainsIds);
		this.ensureInjectedProvider();
		this.refresh();
	}

	#removeUniveralListeners(provider: UniversalProvider): void {
		this.cachedInjectedProvider = undefined;
		provider.removeListener('session_update', this.setNamespaceChainsIds);
		this.reset();
	}
}
